import { useRouter } from 'found';
import styled from 'styled-components';
import React, { useState } from 'react';
import { makeMutation } from '../../../../api/helpers';
import { AddCharge } from '../../../../api/mutations';
import ContentCenterer from '../../../../components/ContentCenterer';
import Button from '../../../../components/input/Button';
import TextInput from '../../../../components/input/TextInput';
import Spacer from '../../../../components/Spacer';
import {
  NormalText,
  PageTitle,
} from '../../../../components/typography/Typography';
import { Buttons } from '../Buttons';
import { useToastDispatch } from '../../../../components/Toast/ToastProvider';
import Dropdown from '../../../../components/input/Dropdown';
import PriceInput from '../../../../components/input/PriceInput';
import Label from '../../../../components/input/Label';
import { mutationsAddChargeMutationVariables } from '../../../../api/__generated__/mutationsAddChargeMutation.graphql';
import { graphql } from 'relay-runtime';
import { createFragmentContainer } from 'react-relay';
import { CreateCharge_service } from './__generated__/CreateCharge_service.graphql';

const Pricing = styled.div`
  display: flex;
  align-items: center;
`;

const PerText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #525067;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

const Currencies = styled.div`
  display: flex;
  flex-direction: column;
`;

const defaultDropdownOption = { label: 'STATIC', value: 'static' };

type Props = {
  service: CreateCharge_service;
};

function CreateCharge({ service }: Props) {
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    prices: service.supportedCurrencies.map((c) => ({ currency: c, price: 0 })),
    metricName: '',
    dropdownOption: defaultDropdownOption,
  });

  const { router, match } = useRouter();
  const dispatch = useToastDispatch();

  // onClose go back to previous page
  const onClose = () => {
    router.go(-1);
  };

  const onSave = async () => {
    if (formState.name.replace(' ', '') === '') {
      dispatch({ text: 'Please enter a charge name', alert: 'error' });
      return;
    }

    for (let i of formState.prices) {
      if (i.price < 0 || isNaN(i.price)) {
        dispatch({ text: 'Please enter a valid price', alert: 'error' });
        return;
      }
    }

    if (
      formState.dropdownOption.value !== 'static' &&
      formState.dropdownOption.value !== 'dynamic'
    ) {
      dispatch({ text: 'Please enter a valid charge type', alert: 'error' });
      return;
    }

    const variables: mutationsAddChargeMutationVariables = {
      serviceID: match.params.serviceID,
      name: formState.name,
      description: formState.description,
      prices: formState.prices,
      metricName:
        formState.metricName === '' ? undefined : formState.metricName,
      chargeType: formState.dropdownOption.value,
    };

    try {
      await makeMutation(AddCharge, variables);
      dispatch({
        text: `'${formState.name}' charge created successfully`,
        alert: 'success',
      });
      onClose();
    } catch (err) {
      dispatch({ text: err.toString(), alert: 'error', stayFor: 4000 });
    }
  };

  const updatePrice = (currency: string, price: number) => {
    setFormState((current) => {
      const newState = { ...current };
      for (const i of newState.prices) {
        if (i.currency === currency) {
          console.log('found', i);
          i.price = price;
        }
      }
      console.log('new', newState);
      return newState;
    });
  };

  return (
    <Container>
      <ContentCenterer maxWidth={'838px'}>
        <Spacer spacing={4} vertical />
        <PageTitle>Create a new charge</PageTitle>
        <Spacer vertical spacing={2} />
        <NormalText>
          An account allows you to both subscribe to and create your own APIs.
        </NormalText>
        <Spacer vertical spacing={4} />
        <TextInput
          label={'NAME'}
          placeholder={'e.g File upload charge'}
          value={formState.name}
          onChange={(text) => setFormState((val) => ({ ...val, name: text }))}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label={'DESCRIPTION'}
          placeholder={'e.g Cost per file uploaded'}
          value={formState.description}
          onChange={(text) =>
            setFormState((val) => ({ ...val, description: text }))
          }
        />
        <Spacer vertical spacing={2} />
        <Dropdown
          label={'CHARGE TYPE'}
          options={[
            {
              label: 'STATIC',
              value: 'static',
            },
            {
              label: 'DYNAMIC',
              value: 'dynamic',
            },
          ]}
          defaultOption={defaultDropdownOption}
          onChange={(option) =>
            setFormState((val) => ({
              ...val,
              dropdownOption: option,
              metricName: '',
            }))
          }
        />
        <Spacer vertical spacing={2} />
        <Label>PRICING</Label>
        <Spacer vertical spacing={1} />
        <Pricing>
          <Currencies>
            {formState.prices.map((price) => (
              <PriceInput
                currency={price.currency}
                value={price.price}
                onChange={(value) => updatePrice(price.currency, value)}
              />
            ))}
          </Currencies>
          <Spacer horizontal spacing={1} />
          {formState.dropdownOption.value === 'static' ? (
            <>
              <PerText>PER REQUEST</PerText>
            </>
          ) : (
            <>
              <PerText>PER</PerText>
              <Spacer horizontal spacing={1} />
              <TextInput
                placeholder='GB, MB, File...'
                value={formState.metricName}
                onChange={(text) =>
                  setFormState((val) => ({ ...val, metricName: text }))
                }
              />
            </>
          )}
        </Pricing>
        <Spacer vertical spacing={4} />
        <Buttons>
          <Button background={'#bab8c5'} color={'white'} onClick={onClose}>
            Cancel
          </Button>
          <Button color={'white'} onClick={onSave}>
            Create Charge
          </Button>
        </Buttons>
        <Spacer vertical spacing={4} />
      </ContentCenterer>
    </Container>
  );
}

export default createFragmentContainer(CreateCharge, {
  service: graphql`
    fragment CreateCharge_service on Service {
      id
      supportedCurrencies
      supportableCurrencies
    }
  `,
});
